import { useCallback, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Layout from "../../components/Layout";
import Loading from "../../components/Loading";
import ModalConfirmRemoveUser from "../../components/ModalConfirmRemoveUser";
import ChangeUserRoleModal from "../../components/ChangeUserRoleModal";
import { useAuth } from "../../hooks/auth";
import { useRefresh } from "../../hooks/refresh";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import type { StaffUserWithRole, StaffUsersByRole } from "../../utils/userRoles";
import StaffUsersTable from "./StaffUsersTable";

import {Container, Content} from './styles';

export default function Users(){
    const { user, access_token, dateToExpires } = useAuth();
    const { addToast } = useToast();
    const { refresh, handleRefresh } = useRefresh();

    const history = useHistory();

    const [users, setUsers] = useState({} as StaffUsersByRole);
    const [loading, setLoading] = useState(true);
    const [userIdToRemove, setUserIdToRemove] = useState(0);
    const [openModalConfirmRemoveUser, setOpenModalConfirmRemoveUser] = useState(false);
    const [loadingApiRequestToRemoveUser, setLoadingApiRequestToRemoveUser] = useState(false);
    const [userToChangeRole, setUserToChangeRole] = useState<StaffUserWithRole | null>(null);

    useEffect(() => {
        async function loadAPI(){
            try{
                const token = await refreshToken(dateToExpires, access_token);

                api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

                const response = await api.get('/api/adm/users', {
                    params: {
                        role: 'staff'
                    }
                });

                setUsers(response.data.data);

                setLoading(false);
            } catch (err:any){
                console.log(err);

                if(!!err.response?.data.message){
                    addToast({
                        title: err.response.data.message,
                        type: 'error'
                    });
                }

                setLoading(false);
            }
        }

        if(!!user){
            loadAPI();
        } else{
            history.push('/');
        }
        // eslint-disable-next-line
    }, [access_token, user, dateToExpires, history, refresh]);

    const handleRemoveUser = useCallback(async() => {
        setLoadingApiRequestToRemoveUser(true);
        
        try{
            const token = await refreshToken(dateToExpires, access_token);

            api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

            const response = await api.delete(`/api/adm/users/${userIdToRemove}`);

            if(!!response.data.success){
                handleRefresh();

                addToast({
                    title: 'Usuário removido!',
                    type: 'success'
                });

                setOpenModalConfirmRemoveUser(false);
            }
        } catch (err:any){
            console.log(err);
        }

        setLoadingApiRequestToRemoveUser(false);
    }, [dateToExpires, access_token, userIdToRemove, handleRefresh, addToast]);

    return(
        <Container>
            <Layout>
                {!!loading && (
                    <Loading />
                )}

                <ModalConfirmRemoveUser 
                    open={openModalConfirmRemoveUser}
                    isLoading={loadingApiRequestToRemoveUser}
                    functionToRemoveUser={handleRemoveUser}
                    functionToCloseModal={() => setOpenModalConfirmRemoveUser(false)}
                />

                {userToChangeRole && (
                    <ChangeUserRoleModal
                        open
                        userId={userToChangeRole.id}
                        userName={userToChangeRole.first_name}
                        currentRoleId={userToChangeRole.roleId}
                        onClose={() => setUserToChangeRole(null)}
                        onSuccess={() => handleRefresh()}
                    />
                )}

                {!loading && (
                    <Content>
                        <h2>Usuários</h2>

                        <StaffUsersTable
                            users={users}
                            onRemoveUser={(userId) => {
                                setOpenModalConfirmRemoveUser(true);
                                setUserIdToRemove(userId);
                            }}
                            onChangeRole={setUserToChangeRole}
                        />

                        <Link to="/create-user">
                            ADICIONAR USUÁRIO
                        </Link>
                    </Content>
                )}
            </Layout>
        </Container>
    );
}
